import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { useDocumentationPanelWidth } from '../../../../../hooks/useDocumentationPanelWidth';
import { useResizeDrag } from '../../../../../hooks/useResizeDrag';
import { commonWrapperStyles, staticViewSx } from './panelWrapperStyles';
import { CONFIGURATION_CANVAS_LEFT_PANEL_WIDTH } from '../../../../pages/configuration-canvas/consts';
import { useExpandedMode } from '../../../../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { DASHBOARD_HEADER_HEIGHT_PX, PAGE_SIDE_PADDING } from '../../../../../constants';

interface ResizablePanelWrapperProps {
  children: React.ReactNode;
  staticView?: boolean;
  crmOrgId: string;
  configurationItemId: string;
}

export const ResizablePanelWrapper: React.FC<ResizablePanelWrapperProps> = ({
  children,
  staticView,
  configurationItemId,
}) => {
  const { panelWidth, updatePanelWidth, minWidth, maxWidth } = useDocumentationPanelWidth();
  const containerRef = useRef<HTMLDivElement>(null);

  const { isCanvasUnited } = useExpandedMode();
  const isCanvasCompactView = isCanvasUnited && !staticView;

  // Use the reusable resize drag hook
  const {
    isResizing,
    isHovering: isHoveringHandle,
    handleResizeStart,
    handleMouseEnter,
    handleMouseLeave,
  } = useResizeDrag({
    onResize: updatePanelWidth,
    minValue: minWidth,
    maxValue: maxWidth,
    calculateDiff: (e: MouseEvent, startX: number) => {
      if (isCanvasCompactView) {
        // Canvas compact view: right border resize, drag right to expand
        return e.clientX - startX;
      } else {
        // Regular/expanded view: left border resize, drag left to expand
        return startX - e.clientX;
      }
    },
  });

  const dynamicStaticViewSx = {
    ...staticViewSx,
    width: `${panelWidth}px`,
    ...(isCanvasUnited && {
      height: `calc(100vh - ${DASHBOARD_HEADER_HEIGHT_PX} - 32px)`, //32px is the env selector margin
      top: `-${PAGE_SIDE_PADDING - 8}px`,
    }),
  };

  return (
    <Box
      ref={containerRef}
      key={configurationItemId}
      data-testid="documentation-card-panel"
      className="documentation-card-panel"
      sx={{
        ...commonWrapperStyles,
        ...(staticView
          ? dynamicStaticViewSx
          : { width: `${panelWidth}px`, left: CONFIGURATION_CANVAS_LEFT_PANEL_WIDTH + 16 }),
      }}
    >
      {/* Resize Handle */}
      {(staticView || isCanvasCompactView) && (
        <Box
          className="panel-resize-handle"
          sx={{
            position: 'absolute',
            [isCanvasCompactView ? 'right' : 'left']: '0px',
            top: 0,
            bottom: 0,
            width: '2px',
            cursor: 'col-resize',
            zIndex: 400,
            transition: 'opacity 0.2s ease-in-out, background-color 0.2s ease-in-out',
            backgroundColor: isHoveringHandle || isResizing ? colors.blue[500] : 'transparent',
            opacity: isHoveringHandle || isResizing ? 1 : 0,
            '&:hover': {
              opacity: 1,
              backgroundColor: colors.blue[500],
            },
          }}
          onMouseDown={(e) => handleResizeStart(e, panelWidth)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      )}

      {children}
    </Box>
  );
};
